import { localizedStrings } from '@core/strings'
import { statuses } from '@core/theme/statuses'
import { Type, plainToInstance } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { IProjectPlan } from './IProjectPlan'
import { ProjectPlanStatus, StatusFilterOption } from './types'
import { ProjectPlanArea } from '../ProjectPlanArea'
import { ProjectLocationStatus } from '../ProjectLocationNode'
import { SiteBoxDocumentPlan } from '@core/entities/SiteBoxDocumentPlan'
import { ImageDataPixelScale } from '@core/entities/SiteBoxDocumentImagesData'
import { SiteBoxDocumentPdfVersion } from '@core/entities/SiteBoxDocumentPdfVersion'
import { Pin } from '@core/entities/skia/Pin'

export class ProjectPlan extends Entity<IProjectPlan> {
  id: number

  locationPlanDocumentDownloadUrl: string

  locationPlanDocumentKey: string

  locationPlanImageDownloadUrl: string

  locationPlanImageKey: string

  status: ProjectPlanStatus

  pdfHeightInPoints: number

  pdfWidthInPoints: number

  imageWidthInPixels: number

  locationNode?: {
    id: string
    name: string
    locationPath: string
    status: ProjectLocationStatus
  }

  @Type(() => ProjectPlanArea)
  areas: ProjectPlanArea[]

  @Type(() => SiteBoxDocumentPlan)
  siteBoxPlanDocument: SiteBoxDocumentPlan

  @Type(() => SiteBoxDocumentPdfVersion)
  siteBoxPlanDocumentVersion: SiteBoxDocumentPdfVersion

  openPermitCount: number

  public static new(payload: unknown): ProjectPlan {
    const entity = plainToInstance(ProjectPlan, payload)

    return entity
  }

  public isPublished(): boolean {
    return this.status === 'PUBLISHED'
  }

  public isEditable(): boolean {
    return this.status === 'UNPUBLISHED'
  }

  public getHumanReadableValueString(
    selectedZoneIds: string[],
    pins: Pin[] = [],
  ): string {
    // Returns a label for the number of pins in a zone
    const getPinLabel = (numberOfPins: number) =>
      numberOfPins > 0
        ? ` (${numberOfPins} ${localizedStrings.pin}${
            numberOfPins > 1 ? 's' : ''
          })`
        : ''

    // This is the label for the pins not in a zone
    const getIndividualPins = () => {
      const individualPins = pins?.filter(
        (pin) =>
          !selectedZoneIds?.find((zone) => pin.areaUuids?.includes(zone)),
      )

      const pinLabel = getPinLabel(individualPins?.length)

      return pinLabel ? `, ${localizedStrings.noZone}${pinLabel}` : ''
    }

    // This is the label for selected zones and pins
    const getSelectedZonesAndPinsLabel = () => {
      return selectedZoneIds?.map((zone) => {
        const pinsInZone = pins?.filter((pin) => pin.areaUuids?.includes(zone))
        const zoneName = this.areas.find((area) => area.uuid === zone)?.name
        return ` ${zoneName}${getPinLabel(pinsInZone.length)}`
      })
    }

    // This is the complete label for all cases
    const getZonePinsLabel = () => {
      let label: string | string[] = localizedStrings.noZonesSelected

      const selectedZonesAndPinsLabel = getSelectedZonesAndPinsLabel()

      if (selectedZonesAndPinsLabel?.length) {
        label = selectedZonesAndPinsLabel.join(', ')
      }

      const individualPinsLabel = getIndividualPins()

      if (individualPinsLabel?.length) {
        label += individualPinsLabel
      }

      return label as string
    }

    return getZonePinsLabel()
  }

  public getStatusChipColor() {
    switch (this.status) {
      case 'ARCHIVED':
        return statuses.archived.color
      case 'UNPUBLISHED':
        return statuses.unpublished.color
      default:
        return 'success'
    }
  }

  public getStatusString(): string {
    switch (this.status) {
      case 'UNPUBLISHED':
        return statuses.unpublished.label
      case 'PUBLISHED':
        return statuses.published.label
      case 'ARCHIVED':
        return statuses.archived.label
      default:
        return this.status
    }
  }

  public getFilterStrings(): StatusFilterOption {
    switch (this.status) {
      case 'UNPUBLISHED':
        return 'Unpublished'
      case 'PUBLISHED':
        return 'Published'
      case 'ARCHIVED':
        return 'Archived'
      default:
        return this.status
    }
  }

  public getDocumentImageUrl(scale: ImageDataPixelScale): string {
    return (
      this.siteBoxPlanDocumentVersion?.getDocumentVersionImageUrl(scale) ||
      this.locationPlanImageDownloadUrl
    )
  }

  public getDocumentImageKey(scale: ImageDataPixelScale): string {
    return (
      this.siteBoxPlanDocumentVersion?.getDocumentVersionImageKey(scale) ||
      this.locationPlanImageKey
    )
  }

  public getDocumentImageTransformData(scale: ImageDataPixelScale): {
    widthPixelsPerPoint: number
    heightPixelsPerPoint: number
  } {
    if (this.siteBoxPlanDocumentVersion) {
      return this.siteBoxPlanDocumentVersion.getDocumentVersionImageTransformData(
        scale,
      )
    }

    const heightToWidthRation = this.pdfHeightInPoints / this.pdfWidthInPoints
    const imageHeightInPixels = this.imageWidthInPixels * heightToWidthRation

    const widthPixelsPerPoint = this.imageWidthInPixels / this.pdfWidthInPoints
    const heightPixelsPerPoint = imageHeightInPixels / this.pdfHeightInPoints

    return {
      widthPixelsPerPoint,
      heightPixelsPerPoint,
    }
  }

  public getDocumentImageDimensions(scale: ImageDataPixelScale): {
    width: number
    height: number
  } {
    if (this.siteBoxPlanDocumentVersion) {
      return this.siteBoxPlanDocumentVersion.getDocumentVersionImageDimensions(
        scale,
      )
    }

    const heightToWidthRation = this.pdfHeightInPoints / this.pdfWidthInPoints
    const imageHeightInPixels = this.imageWidthInPixels * heightToWidthRation

    return {
      width: this.imageWidthInPixels,
      height: imageHeightInPixels,
    }
  }
}
